import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useIntl } from 'react-intl';

import Box from 'carbon-react/lib/components/box';
import Textbox from 'carbon-react/lib/components/textbox';
import { Option, Select } from 'carbon-react/lib/components/select';

import { getReportingEditTemplateUrl } from '@futrli/shared-services';
import { LoadingWizardScreen } from '@futrli/ui-components/components/sidebar/wizard/screens/loading-wizard-screen/loading-wizard-screen';
import {
  FooterMode,
  WizardScreen
} from '@futrli/ui-components/components/sidebar/wizard/screens/wizard-screen/wizard-screen';
import { WizardFinished } from '@futrli/ui-components/components/sidebar/wizard/wizard-finished/wizard-finished';
import { WizardFormError } from '@futrli/ui-components/components/sidebar/wizard/wizard-form-error/wizard-form-error';
import { WizardFormValidationErrorSummary } from '@futrli/ui-components/components/sidebar/wizard/wizard-form-validation-error-summary/wizard-form-validation-error-summary';
import { WizardRequiredFieldInfo } from '@futrli/ui-components/components/sidebar/wizard/wizard-required-field-info/wizard-required-field-info';

import { useApiView } from '@futrli/views/hooks/useApiView';
import { ModifyReportTemplateView } from '@futrli/views/views/reports/ModifyReportTemplateView/ModifyReportTemplateView';

import { useRouteIds } from '../../../hooks/useRouteIds';
import { usePredictFlags } from '../../../hooks/usePredictFlags';

type Screen = 'form' | 'creating' | 'created';
type Orientation = 'portrait' | 'landscape' | 'dashboard';

const NAME_FIELD_ID = 'futrli-template-name';
const TYPE_FIELD_ID = 'futrli-template-type';

export interface CreateFutrliTemplateWizardContainerProps {
  onClose: () => void;
}

export const CreateFutrliTemplateWizardContainer = ({ onClose }: CreateFutrliTemplateWizardContainerProps) => {
  const navigate = useNavigate();
  const intl = useIntl();
  const { hasSwitchLocaleEnabled } = usePredictFlags();
  const { workspaceId, organisationId, isConsolidation } = useRouteIds();

  const view = useApiView(
    useMemo(() => new ModifyReportTemplateView(workspaceId, organisationId, isConsolidation), [
      workspaceId,
      organisationId,
      isConsolidation
    ])
  );

  const [screen, setScreen] = useState<Screen>('form');
  const [error, setError] = useState<string | undefined>();
  const [templateName, setTemplateName] = useState('');
  const [orientation, setOrientation] = useState<Orientation>('portrait');
  const [validationErrors, setValidationErrors] = useState<Record<string, { id: string; message: string }>>({});

  useEffect(() => {
    if (view.state.isCreatingTemplate) {
      setScreen('creating');
    }
  }, [view.state.isCreatingTemplate]);

  useEffect(() => {
    if (view.state.newTemplateId) {
      setScreen('created');
    }
  }, [view.state.newTemplateId]);

  useEffect(() => {
    if (view.state.errorCreatingTemplate) {
      setError(intl.formatMessage({ id: 'reporting.try_again', defaultMessage: 'Try again.' }));
      setScreen('form');
    }
  }, [view.state.errorCreatingTemplate, intl]);

  const validate = useCallback(() => {
    const errors: Record<string, { id: string; message: string }> = {};
    if (!templateName.trim()) {
      errors[NAME_FIELD_ID] = {
        id: NAME_FIELD_ID,
        message: intl.formatMessage({
          id: 'reporting.validation.template_name_is_required',
          defaultMessage: 'Template name is required'
        })
      };
    }
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  }, [templateName, intl]);

  const handleCreate = useCallback(() => {
    if (!validate()) {
      return;
    }
    setError(undefined);
    view.createFutrliTemplate({
      name: templateName.trim(),
      orientation,
      locale: hasSwitchLocaleEnabled ? intl.locale : undefined
    });
  }, [validate, view, templateName, orientation, hasSwitchLocaleEnabled, intl.locale]);

  const handleView = useCallback(() => {
    if (view.state.newTemplateId) {
      onClose();
      navigate(getReportingEditTemplateUrl(workspaceId, organisationId, view.state.newTemplateId, isConsolidation));
    }
  }, [view.state.newTemplateId, navigate, workspaceId, organisationId, isConsolidation, onClose]);

  if (screen === 'creating') {
    return (
      <LoadingWizardScreen
        text={intl.formatMessage({ id: 'reporting.creating_template', defaultMessage: 'Creating template...' })}
      />
    );
  }

  if (screen === 'created') {
    return (
      <WizardScreen
        footerMode={FooterMode.CREATED}
        onCancel={onClose}
        onView={handleView}
        viewText={intl.formatMessage({ id: 'reporting.go_to_template', defaultMessage: 'Go to template' })}
      >
        <WizardFinished
          text={intl.formatMessage({
            id: 'reporting.futrli_template_created_and_saved_as_draft',
            defaultMessage: 'Futrli template created and saved as a draft'
          })}
        />
      </WizardScreen>
    );
  }

  return (
    <WizardScreen
      footerMode={FooterMode.CREATE}
      onCancel={onClose}
      onCreate={handleCreate}
      createText={intl.formatMessage({ id: 'common.create', defaultMessage: 'Create' })}
    >
      <>
        {error && <WizardFormError message={error} mb={3} />}

        <WizardFormValidationErrorSummary errors={validationErrors} mb={3} />

        <WizardRequiredFieldInfo />

        <Box mb={3}>
          <Textbox
            label={intl.formatMessage({ id: 'reporting.template_name', defaultMessage: 'Template name' })}
            name={NAME_FIELD_ID}
            id={NAME_FIELD_ID}
            value={templateName}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTemplateName(e.target.value)}
            error={validationErrors[NAME_FIELD_ID]?.message}
            required
          />
        </Box>

        <Box mb={3}>
          <Select
            label={intl.formatMessage({ id: 'reporting.template_type', defaultMessage: 'Template type' })}
            name={TYPE_FIELD_ID}
            id={TYPE_FIELD_ID}
            value={orientation}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setOrientation(e.target.value as Orientation)}
          >
            <Option
              value="portrait"
              text={intl.formatMessage({ id: 'reporting.portrait', defaultMessage: 'Portrait' })}
            />
            <Option
              value="landscape"
              text={intl.formatMessage({ id: 'reporting.landscape', defaultMessage: 'Landscape' })}
            />
            <Option
              value="dashboard"
              text={intl.formatMessage({ id: 'reporting.dashboard', defaultMessage: 'Dashboard' })}
            />
          </Select>
        </Box>
      </>
    </WizardScreen>
  );
};
